
import ReportQuickView from '@/components/Report/ReportQuickView.vue'
import { defineComponent, watch, ref } from 'vue'
import { AttensiButton, AttensiInput, AttensiCard } from '@attensi/materia'
import { useRouter } from 'vue-router'
import { createMyReportsWizardRoute } from '@/router/createRoute'
import { nanoid } from 'nanoid'
import { getApolloClient } from '@/apollo-client'
import { ApolloError } from 'apollo-client'
import { provideApolloClient, useQuery } from '@vue/apollo-composable'
import {
  getReportsQuery,
  getReportByIdQuery,
  deleteReportMutation,
  getSchedulesQuery,
  getUsersQuery,
} from '@/graphql-operations/index'
import { Report, unlockedLock } from '@/models/model.report'
import { canManageReportLock } from '@/helpers/helper.userAccess'
import {
  currentUserOption,
  toImpersonatableUserOptions,
  ImpersonatableUserOption,
} from '@/helpers/helper.impersonatableUsers'
import ImpersonatableUserPicker from '@/components/Report/ImpersonatableUserPicker.vue'
import { useUser } from '@/swr/swr.user'
import ReportTypeBadge from '@/components/ReportTypeBadge.vue'
import { toast } from '@/use/use.toast'
import Modal from '@/components/Modal.vue'
import ReportDownloadButton from '@/components/ReportDownloadButton.vue'
import NoDataModal from '@/components/NoDataModal.vue'
import { useI18n } from 'vue-i18n'
import MenuIcon from '@/components/Icons'
import { onClickOutside } from '@vueuse/core'
import RepetitionsVisualizationModal from '@/components/RepetitionsVisualizationModal.vue'
import KnowledgeVisualizationModalVue from '@/components/KnowledgeVisualizationModal.vue'
import PlaythroughsVisualizationModal from '@/components/PlaythroughsVisualizationModal.vue'
import { Schedule, ScheduleEntity } from '@/models/model.schedule'
import lodash from 'lodash'
import { FilterGroup } from '@/models/model.filterGroup'
import { INSIGHTS_SCHEDULER_API } from '@/consts'
import { getAccessToken } from '@/auth'
import axios from 'axios'
import { gql } from 'graphql-tag'
import { INSIGHTS_BACKEND_API } from '@/consts'
import { realTalkUserTranscriptReportEnabled } from '@/unleashClient'
import { downloadReportLogs } from '@/helpers/helper.logs'
import ErrorComponent from '@/components/ErrorComponent.vue'
import LoadingComponent from '@/components/LoadingComponent.vue'
import { reportPreviewEnabled } from '@/unleashClient'

export default defineComponent({
  components: {
    AttensiButton,
    AttensiInput,
    ReportTypeBadge,
    Modal,
    ReportDownloadButton,
    NoDataModal,
    ImpersonatableUserPicker,
    AttensiCard,
    MenuIcon,
    RepetitionsVisualizationModal,
    KnowledgeVisualizationModalVue,
    PlaythroughsVisualizationModal,
    ReportQuickView,
    ErrorComponent,
    LoadingComponent,
  },

  setup() {
    const router = useRouter()
    const isModalOpened = ref(false)
    const isQuickViewModalOpened = ref(false)
    const closeModal = () => (isModalOpened.value = false)
    const closeQuickViewModal = () => (isQuickViewModalOpened.value = false)
    const createNewReport = () => {
      const route = createMyReportsWizardRoute('new', nanoid())
      router.push(route)
    }
    const { t } = useI18n()
    const selectedItem = ref<null | number>(null)
    const dropdownContainer = ref<HTMLDivElement | null>(null)
    const isGraphModalOpened = ref<boolean>(false)
    const selectedReport = ref<Report | null>(null)
    const visualizationHeadingText = ref<string>('')
    const visualizationHelperText = ref<string>('')
    const showNoData = ref<boolean>(false)
    const isImpersonatedUserNoDataModalOpened = ref<boolean>(false)
    const isShareReportModalOpened = ref(false)
    const closeShareReportModal = () => (isShareReportModalOpened.value = false)
    const closeImpersonatedUserNoDataModal = () =>
      (isImpersonatedUserNoDataModalOpened.value = false)

    const handleImpersonatedUserNoDataModal = (showNoData: boolean) => {
      isImpersonatedUserNoDataModalOpened.value = showNoData
      if (showNoData) {
        isImpersonatableUsersModalOpened.value = false
      }
    }

    const loadingData = ref({
      reports: true,
      users: true,
    })

    const errorData = ref({
      reports: false,
      users: false,
    })

    const showNoDataModel = (sd: boolean) => {
      isGraphModalOpened.value = false
      showNoData.value = sd
    }

    const toggleDropdown = (index: number) => {
      selectedItem.value === index
        ? (selectedItem.value = null)
        : (selectedItem.value = index)
    }

    onClickOutside(dropdownContainer, (event) => {
      selectedItem.value = null
    })

    const showGrapghModal = (report: Report) => {
      selectedReport.value = report
      isGraphModalOpened.value = true

      switch (report.type) {
        case 'repetitions':
          visualizationHeadingText.value = t(
            'visualizations.REPETITIONS_HEADING'
          )
          visualizationHelperText.value = t(
            'visualizations.REPETITIONS_HELPER_TEXT'
          )
          break
        case 'knowledge':
          visualizationHeadingText.value = visualizationHeadingText.value = t(
            'visualizations.KNOWLEDGE_HEADING'
          )
          visualizationHelperText.value = t(
            'visualizations.KNOWLEDGE_HELPER_TEXT'
          )
          break
        case 'playthroughs':
          visualizationHeadingText.value = t(
            'visualizations.PLAYTHROUGHS_HEADING'
          )
          visualizationHelperText.value = t(
            'visualizations.PLAYTHROUGHS_HELPER_TEXT'
          )
          break
        default:
          break
      }
    }

    const isImpersonatableUsersModalOpened = ref(false)
    const impersonatableUsers = ref<ImpersonatableUserOption[]>([
      currentUserOption(),
    ])

    // Set default impersonated user to "Current User"
    const selectedImpersonatedUser = ref<ImpersonatableUserOption>(
      currentUserOption()
    )

    const closeImpersonatedUserModal = () =>
      (isImpersonatableUsersModalOpened.value = false)

    return {
      isModalOpened,
      closeModal,
      createNewReport,
      router,
      t,
      toggleDropdown,
      selectedItem,
      dropdownContainer,
      isGraphModalOpened,
      selectedReport,
      showGrapghModal,
      visualizationHelperText,
      visualizationHeadingText,
      showNoData,
      showNoDataModel,
      lodash,
      isShareReportModalOpened,
      closeShareReportModal,
      isQuickViewModalOpened,
      closeQuickViewModal,
      realTalkUserTranscriptReportEnabled,
      downloadReportLogs,
      loadingData,
      errorData,
      impersonatableUsers,
      isImpersonatableUsersModalOpened,
      selectedImpersonatedUser,
      closeImpersonatedUserModal,
      isImpersonatedUserNoDataModalOpened,
      closeImpersonatedUserNoDataModal,
      handleImpersonatedUserNoDataModal,
      reportPreviewEnabled,
      ...useUser(),
    }
  },

  data() {
    return {
      error: null as ApolloError | null,
      loading: [] as boolean[],
      search: '',
      reports: [] as Report[],
      reportBlob: null as any,
      reportToDelete: null as Report | null,
      reportSchedules: [] as any[],
      users: [] as FilterGroup[],
      selectedUsers: [] as FilterGroup[],
      searchUsers: '',
      sharedReport: {} as Report,
      reportQuickView: {} as Report,
    }
  },

  computed: {
    isMobile() {
      return window.innerWidth < 1200
    },

    filteredReports(): Report[] {
      if (this.search.length <= 0) return this.reports
      return this.reports.filter((r) => {
        const searchLowerCase = this.search?.toLowerCase()
        return (
          r.title?.toLowerCase().includes(searchLowerCase) ||
          r.description?.toLocaleLowerCase().includes(searchLowerCase) ||
          r.type?.toLocaleLowerCase() === searchLowerCase
        )
      })
    },

    filteredUsers(): FilterGroup[] {
      if (this.searchUsers.length <= 0) return this.users
      return this.users.filter(
        (x) =>
          x.name?.toLowerCase().includes(this.searchUsers?.toLowerCase()) ||
          x.description?.toLowerCase().includes(this.searchUsers?.toLowerCase())
      )
    },

    // id -> name lookup built once per users change, so per-card name
    // resolution (getUserName) is O(1) instead of scanning the list each time.
    userNameById(): Record<number, string> {
      return this.users.reduce((acc, u) => {
        acc[u.id] = u.name
        return acc
      }, {} as Record<number, string>)
    },
  },

  async created() {
    provideApolloClient(await getApolloClient())
    await this.loadReports()
    await this.loadUsers()
  },

  methods: {
    openQuickViewModal: function (report: Report) {
      this.reportQuickView = report
      this.isQuickViewModalOpened = true
    },
    createScheduleForReport: function (report: Report) {
      let schedule = {} as Schedule
      schedule.reports = [{ id: report.id } as Report]

      this.$router.push({
        name: 'CreateSchedule',
        params: { scheduleCopy: JSON.stringify(schedule) },
      })
    },

    loadUsers: async function () {
      const { result } = useQuery(getUsersQuery())

      const alphabeticalNameSort = (a: { name: string }, b: { name: string }) =>
        a.name?.toLowerCase() > b.name?.toLowerCase() ? 1 : -1

      watch(result, (value) => {
        this.users = value['users']
          .map((x: any) => ({
            id: x.user_id,
            name: x.name,
            description: x.email,
          }))
          .sort(alphabeticalNameSort) as FilterGroup[]
      })
    },

    // Resolve a user id to a display name using the same active-user list
    // already loaded for the share dialog. Returns undefined when the id is
    // missing or the user isn't in the list (e.g. inactive), so callers can
    // hide the field.
    getUserName(userId?: number): string | undefined {
      if (!userId) return undefined
      return this.userNameById[userId]
    },

    clickShare: async function () {
      const apiUrl = `${INSIGHTS_SCHEDULER_API}/jobs/SendReportToUsers`
      const jwtToken = await getAccessToken()

      const requestData = {
        ReportId: this.sharedReport.id,
        UserIds: this.selectedUsers.map((x) => x.id),
      }

      // Headers with the JWT Bearer token
      const headers = {
        Authorization: `Bearer ${jwtToken}`,
        'Content-Type': 'application/json',
      }

      axios
        .post(apiUrl, requestData, { headers })
        .then((response) => {
          if (response.status === 204) {
            toast(
              {
                title: this.t('report-home-page.SHARE_REPORT_HEADING'),
                text: this.t('report-home-page.SHARE_REPORT_SUCCESS').replace(
                  '[report_title]',
                  this.sharedReport.title
                ),
              },
              undefined
            )

            this.isShareReportModalOpened = false
          }
        })
        .catch((error) => {
          console.error('Error:', error.message)
        })
    },

    loadReports: async function (): Promise<void> {
      try {
        const result = await getReportsQuery()

        let reports = (result as unknown as { reports: any }).reports

        if (!realTalkUserTranscriptReportEnabled())
          reports = reports.filter(
            (x: any) => x.config.type !== 'realTalkUserTranscript'
          )

        reports = reports.map(
          (r: any) =>
            ({
              id: r.id,
              ownerId: r.owner_id,
              updatedBy: r.updated_by,
              updatedAt: r.version_created_at,
              created: r.created_at,
              lock: r.config?.lock ?? unlockedLock(),
              title: r.title,
              description: r.description,
              type: r.config?.type,
              selectedDepartments: r.config?.selectedDepartments ?? [],
              selectedCustomGroups: r.config?.selectedCustomGroups ?? [],
              selectedPositions: r.config?.selectedPositions ?? [],
              selectedModules: r.config?.selectedModules ?? [],
              selectedModuleCategories:
                r.config?.selectedModuleCategories ?? [],
              selectedProducts: r.config?.selectedProducts ?? [],
              selectedUsers: r.config?.selectedUsers ?? [],
              selectedInstructors: r.config?.selectedInstructors ?? [],
              selectedEventInstances: r.config?.selectedEventInstances ?? [],
              portalCategories: r.config?.portalCategories ?? [],
              activeStatus: r.config?.activeStatus,
              reportStatus: r.config?.reportStatus,
              timeframe_type: r.config?.timeframe_type,
              timeframe_from: r.config?.timeframe_from,
              timeframe_to: r.config?.timeframe_to,
              formatedReport: r.config?.formatedReport,
              selectedFields: r.config?.selectedFields,
              userDetails: r.config.userDetails ?? [],
              calculatedFields: r.config.calculatedFields ?? [],
              dataField: r.config.dataField,
              orderByField: r.config.orderByField,
              orderByDirection: r.config.orderByDirection,
              dynamicReport: r.config.dynamicReport,
              dynamicUserLevelReport: r.config.dynamicUserLevelReport ?? null,
              includeChildrenDepartments:
                r.config.includeChildrenDepartments ?? null,
              attended: r.config.attended,
              mandatory: r.config.mandatory,
              language: r.config.language,
              eventInstancesLastDate: r.config.eventInstancesLastDate,
              eventInstancesFutureDate: r.config.eventInstancesFutureDate,
              certificationStatus: r.config.certificationStatus,
              departmentLevelsCompletion:
                r.config.departmentLevelsCompletion ?? [],
            } as Report)
        )

        if (!lodash.some(reports)) {
          try {
            await this.setSampleReports()
            return this.loadReports()
          } catch (e) {
            console.warn('setSampleReports failed', e)
            this.createNewReport()
          }
        }

        this.reports = reports
      } catch (error) {
        this.errorData.reports = true
      } finally {
        this.loadingData.reports = false
      }
    },

    setSampleReports: async function () {
      const url = `${INSIGHTS_BACKEND_API}/report-frontend/setSampleReports`

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: await getAccessToken(),
        },
      })

      if (!response.ok) {
        throw new Error(
          `setSampleReports failed with status ${response.status}`
        )
      }
    },

    editReport: function (reportId: string) {
      this.router.push({
        name: 'EditReport',
        params: { reportId },
      })
    },

    // Only the report's creator (owner) may lock/unlock it (the toggle lives in
    // the report editor). No admin override — not even superusers/superadmins.
    canManageLock: function (report: Report): boolean {
      return canManageReportLock(this.user, report)
    },

    // Edit is disabled for a locked report unless the user can unlock it (they do
    // so via the toggle in the editor). Locked reports can always be duplicated.
    isEditLocked: function (report: Report): boolean {
      return report.lock?.isLocked === true && !this.canManageLock(report)
    },

    // Delete is disabled for a locked report unless the user can unlock it (the
    // creator), mirroring isEditLocked. Others must duplicate the report instead.
    isDeleteLocked: function (report: Report): boolean {
      return report.lock?.isLocked === true && !this.canManageLock(report)
    },

    onEditClick: function (report: Report) {
      if (this.isEditLocked(report)) return
      this.editReport(report.id)
    },

    onDeleteClick: function (report: Report) {
      if (this.isDeleteLocked(report)) return
      this.openModal(report)
    },

    getReportSchedules: function (report: Report, schedules: any): any {
      const reportSchedules = schedules.filter((schedule: ScheduleEntity) => {
        const isReportUsedBySchedule = schedule.config.reportIds.find(
          (id) => id === report.id
        )

        if (isReportUsedBySchedule) return schedule
      })

      return reportSchedules
    },

    async openImpersonatableUsersModal(report: Report) {
      this.reportQuickView = report
      this.isImpersonatableUsersModalOpened = true
      await this.getImpersonatableUsers()

      // Reset selected user to "Current User"
      this.selectedImpersonatedUser = currentUserOption()
    },

    async getImpersonatableUsers() {
      try {
        const url = `${INSIGHTS_BACKEND_API}/user/impersonatableUsers`

        const response = await fetch(url, {
          headers: {
            Authorization: await getAccessToken(),
          },
        })

        this.impersonatableUsers = toImpersonatableUserOptions(
          await response.json()
        )
      } catch (error) {
        console.error(
          'Failed to load impersonatable users:',
          error instanceof Error ? error.message : String(error)
        )
      }
    },

    copyReport: async function (reportId: string) {
      // Clone from the latest version of the source report; getReportByIdQuery
      // owns the latest-version resolution.
      const [getReportQuery, getReportVariables] = getReportByIdQuery(reportId)

      const CREATE_REPORT = gql`
        mutation insert_report(
          $id: String!
          $config: jsonb!
          $title: String!
          $description: String!
        ) {
          insert_reports_one(
            object: {
              id: $id
              config: $config
              title: $title
              description: $description
            }
          ) {
            id
          }
        }
      `

      // Fetch the existing report's data
      const { result, error } = useQuery(getReportQuery, getReportVariables)
      const newReportId = nanoid()

      watch(result, (value) => {
        // Apollo results are frozen — don't mutate `report`, derive the
        // clone's fields instead.
        const report = lodash.first(value.reports) as any

        // A clone is always unlocked, regardless of the source report's lock state.
        const clonedConfig = { ...(report.config ?? {}) }
        delete clonedConfig.lock

        // Insert a new report with the fetched data
        const { result, error } = useQuery(CREATE_REPORT, {
          id: newReportId,
          config: clonedConfig,
          title: `${report.title} (${this.t('copy-report.COPY_TITLE')})`,
          description: report.description,
        })

        watch(result, (value) => {
          this.router.push({
            name: 'EditReport',
            params: { reportId: newReportId },
          })
        })

        watch(error, (value) => {
          this.error = value
          const title = this.t('copy-report.MODAL_ERROR_TITLE')
          const text = this.t('copy-report.MODAL_ERROR_MESSAGE')
        })
      })
    },

    openModal: function (report: Report) {
      const { result } = useQuery(getSchedulesQuery())

      watch(result, () => {
        this.reportSchedules = this.getReportSchedules(
          report,
          result.value.schedules
        )

        this.reportToDelete = report
        this.isModalOpened = true
      })
    },

    deleteReport: function () {
      const { result } = useQuery(getSchedulesQuery())

      watch(result, () => {
        this.reportSchedules = this.getReportSchedules(
          this.reportToDelete as Report,
          result.value.schedules
        )

        if (!lodash.isEmpty(this.reportSchedules)) return

        const reportId = this.reportToDelete?.id as string

        var reportIndex = this.reports
          .map((report) => report.id)
          .indexOf(reportId)

        const executeDelete = () => {
          const [query, variables] = deleteReportMutation(reportId)
          const { result, error } = useQuery(query, variables)

          watch(error, (e) => {
            this.reportToDelete = null
            throw new Error(`${e}`)
          })
          watch(result, (r) => {
            if (r !== undefined) {
              this.reports.splice(reportIndex, 1)
              toast(
                {
                  title: 'Delete Report',
                  text: `'${this.reportToDelete?.title}' was deleted successfully!`,
                },
                undefined
              )
              this.reportToDelete = null
            } else {
              throw new Error(
                `The report could not be deleted: reportid: ${reportId}`
              )
            }
          })
          this.isModalOpened = false
        }

        executeDelete()
      })
    },

    openModalShareReport: function (report: Report) {
      this.selectedUsers = []
      this.searchUsers = ''
      this.sharedReport = report
      this.isShareReportModalOpened = true
    },

    selectUser: function (user: FilterGroup) {
      const index = this.selectedUsers.findIndex((x) => x.id === user.id)
      index >= 0
        ? this.selectedUsers.splice(index, 1)
        : this.selectedUsers.push(user)
    },
  },
})
